import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import AuthContext from "../../context/AuthContext";
import useCustomForm from "../../hooks/useCustomForm";
import "./LoginPage.css";

const AUTH_API = (process.env.REACT_APP_API_URL || "") + "/api/auth";

const LoginPage = () => {
  const {
    loginUser, verifyTotp, totpPending, isServerError, loginErrorMessage, loginLoading,
    passkeySetupPending, skipPasskeySetup, registerPasskey, loginWithPasskey,
  } = useContext(AuthContext);
  const defaultValues = { username: "", password: "" };
  const [formData, handleInputChange, handleSubmit, reset] = useCustomForm(defaultValues, loginUser);
  const [totpCode, setTotpCode] = useState("");
  const [totpError, setTotpError] = useState("");

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const [passkeyError, setPasskeyError] = useState("");
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState("");

  const supportsPasskeys = typeof window !== "undefined" && !!window.PublicKeyCredential;

  useEffect(() => {
    if (isServerError) {
      reset();
      setTotpCode("");
      setTotpError("");
    }
  }, [isServerError]);

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setTotpError("");
    try {
      await verifyTotp(totpCode);
    } catch {
      setTotpError("Invalid code. Please try again.");
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotMsg("");
    setForgotLoading(true);
    try {
      const res = await axios.post(`${AUTH_API}/password-reset/`, { email: forgotEmail });
      setForgotMsg(res.data.message);
    } catch {
      setForgotError("Something went wrong. Please try again.");
    }
    setForgotLoading(false);
  };

  const handlePasskeyLogin = async () => {
    if (!formData.username) {
      setPasskeyError("Enter your username first.");
      return;
    }
    setPasskeyError("");
    setPasskeyLoading(true);
    const result = await loginWithPasskey(formData.username);
    if (result?.error && result.error !== "Cancelled") setPasskeyError(result.error);
    setPasskeyLoading(false);
  };

  const handleSetupPasskey = async () => {
    setSetupError("");
    setSetupLoading(true);
    const result = await registerPasskey("");
    if (result?.error && result.error !== "Cancelled") setSetupError(result.error);
    setSetupLoading(false);
  };

  // — Passkey setup prompt (shown after password login on biometric-capable devices) —
  if (passkeySetupPending) {
    return (
      <div className="container">
        <div className="form" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🔒</div>
          <h2 style={{ marginTop: 0 }}>Set up Face ID / Touch ID</h2>
          <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: "1.5rem" }}>
            Sign in faster next time using your fingerprint or face — no password needed.
          </p>
          {setupError && <p className="error">{setupError}</p>}
          <button
            onClick={handleSetupPasskey}
            disabled={setupLoading}
            style={{ width: "100%", marginBottom: "0.75rem" }}
          >
            {setupLoading ? "Setting up…" : "Set up Face ID / Touch ID"}
          </button>
          <button
            type="button"
            onClick={skipPasskeySetup}
            style={{ width: "100%", background: "none", border: "none", color: "#666", cursor: "pointer", textDecoration: "underline", fontSize: "0.85rem" }}
          >
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  // — TOTP step —
  if (totpPending) {
    return (
      <div className="container">
        <form className="form" onSubmit={handleTotpSubmit}>
          <h2 style={{ marginBottom: "0.5rem" }}>Two-Factor Authentication</h2>
          <p style={{ fontSize: "0.9rem", color: "#555", marginBottom: "1rem" }}>
            Enter the 6-digit code from your authenticator app.
          </p>
          <label>
            Code:{" "}
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={totpCode}
              onChange={e => setTotpCode(e.target.value)}
              autoFocus
              placeholder="000000"
            />
          </label>
          {(isServerError || totpError) && (
            <p className="error">Invalid code. Please try again.</p>
          )}
          <button type="submit">Verify</button>
        </form>
      </div>
    );
  }

  // — Forgot password step —
  if (showForgot) {
    return (
      <div className="container">
        <form className="form" onSubmit={handleForgotSubmit}>
          <h2 style={{ marginBottom: "0.5rem" }}>Reset Password</h2>
          <p style={{ fontSize: "0.9rem", color: "#555", marginBottom: "1rem" }}>
            Enter your email address and we'll send you a reset link.
          </p>
          {forgotMsg ? (
            <>
              <p style={{ color: "green", textAlign: "center" }}>{forgotMsg}</p>
              <button type="button" onClick={() => { setShowForgot(false); setForgotMsg(""); setForgotEmail(""); }}>
                Back to Login
              </button>
            </>
          ) : (
            <>
              <label>
                Email:{" "}
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                />
              </label>
              {forgotError && <p className="error">{forgotError}</p>}
              <button type="submit" disabled={forgotLoading}>
                {forgotLoading ? "Sending..." : "Send Reset Link"}
              </button>
              <button type="button" style={{ marginTop: "0.5rem", background: "none", border: "none", color: "#666", cursor: "pointer", textDecoration: "underline", fontSize: "0.85rem" }}
                onClick={() => { setShowForgot(false); setForgotError(""); }}>
                Back to Login
              </button>
            </>
          )}
        </form>
      </div>
    );
  }

  // — Main login form —
  return (
    <div className="container">
      <form className="form" onSubmit={handleSubmit}>
        <label>
          Username:{" "}
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
          />
        </label>
        <label>
          Password:{" "}
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
          />
        </label>
        {isServerError && <p className="error">{loginErrorMessage || "Something went wrong. Please try again."}</p>}
        {passkeyError && <p className="error">{passkeyError}</p>}
        <button type="submit" disabled={loginLoading}>{loginLoading ? "Signing in…" : "Login"}</button>

        {supportsPasskeys && (
          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={passkeyLoading}
            style={{
              marginTop: "0.5rem", width: "100%",
              background: "#f0f4ff", border: "1px solid #c7d2fe",
              borderRadius: 6, padding: "0.5rem", cursor: "pointer",
              color: "#3730a3", fontWeight: 600, fontSize: "0.95rem",
            }}
          >
            {passkeyLoading ? "Waiting for biometric…" : "Sign in with Face ID / Touch ID"}
          </button>
        )}

        <button
          type="button"
          style={{ marginTop: "0.5rem", background: "none", border: "none", color: "#666", cursor: "pointer", textDecoration: "underline", fontSize: "0.85rem" }}
          onClick={() => setShowForgot(true)}>
          Forgot password?
        </button>
      </form>
    </div>
  );
};

export default LoginPage;
